// src/components/common/NetworkSnackbar.tsx

import React, { useState, useEffect } from 'react';
import { Snackbar, Alert, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close'; // Import the close icon
import { useSelector } from 'react-redux';
import { RootState } from '@store/store';

export const NetworkSnackbar: React.FC = () => {
    // 1. Get global network status from Redux
    const { isOnline, isWeak } = useSelector((state: RootState) => state.network);

    // 2. Create local state to control the Snackbar's visibility
    const [isSnackbarOpen, setIsSnackbarOpen] = useState(false);

    // 3. Use useEffect to show the snackbar when the network status changes to bad
    useEffect(() => {
        // If the network is offline or weak, trigger the snackbar to open
        if (!isOnline || isWeak) {
            setIsSnackbarOpen(true);
        }
        // If the network becomes healthy again, automatically close the snackbar
        if (isOnline && !isWeak) {
            setIsSnackbarOpen(false);
        }
    }, [isOnline, isWeak]); // This effect runs whenever the global network state changes

    // 4. Handle the close event from the Snackbar or the close button
    const handleClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
        // Prevent closing if the user clicks away from the snackbar
        if (reason === 'clickaway') {
            return;
        }
        setIsSnackbarOpen(false);
    };

    const isOffline = !isOnline;
    const message = isOffline
        ? "No internet connection detected. Please check your network settings and try again."
        : "Your internet connection is unstable. Some features may not work properly. Please check your connection.";
    const severity = isOffline ? 'error' : 'warning';
    const autoHideDuration = 2000;

    return (
        <Snackbar
            // Use the local state for the 'open' prop
            open={isSnackbarOpen}
            autoHideDuration={autoHideDuration}
            // Use the handleClose function for the 'onClose' event
            onClose={handleClose}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            sx={{ mt: '50px' }}
        >
            <Alert
                severity={severity}
                sx={{
                    width: '100%',
                    ...(severity === 'warning' && { backgroundColor: '#ff9800', color: '#fff', '.MuiAlert-icon': { color: '#fff' } }),
                }}
                // 5. Add the close button using the 'action' prop
                action={
                    <IconButton
                        aria-label="close"
                        color="inherit" // The icon will inherit the alert's text color
                        size="small"
                        onClick={handleClose}
                    >
                        <CloseIcon fontSize="inherit" />
                    </IconButton>
                }
            >
                {message}
            </Alert>
        </Snackbar>
    );
};